import React from "react";
import { motion } from "framer-motion";

const STATS = [
  { n: "PPAI 100", label: "Consortium Gifts ranked 81" },
  { n: "41", label: "Countries, IGC Presidency" },
  { n: "100K+", label: "Network Reach" },
];

const ROLES = [
  { label: "Founder — Consortium Gifts" },
  { label: "President — IGC Global Promotions" },
  { label: "Founder — Gaurav Bhagat Academy" },
  { label: "Online — GauravBhagat.com", href: "https://www.gauravbhagat.com" },
  { label: "Podcast — Let's Chat with GB", href: "https://www.youtube.com/@LetsChatWithGB" },
];

export default function WhosBehind() {
  return (
    <section
      id="whos-behind"
      data-testid="whos-behind-section"
      className="relative bg-cream py-16 lg:py-24"
    >
      <div className="max-w-[1400px] mx-auto container-x">
        {/* eyebrow */}
        <div className="flex items-center gap-4 mb-14">
          <span className="font-mono text-[10px] tracking-[0.28em] text-burgundy">WHO&rsquo;S BEHIND THIS</span>
          <span className="flex-1 h-px bg-line" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="font-display font-semibold text-[clamp(2rem,5vw,4rem)] leading-[1.04] tracking-[-0.015em] text-ink max-w-[24ch] mb-16 lg:mb-24"
        >
          The voice of a leader. The pen of an author. A masterclass written
          from the inside.
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* portrait */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="relative aspect-[1/1.15] bg-cream-2 border border-line w-full max-w-lg overflow-hidden"
              data-testid="founder-portrait"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_we-gotta-gift/artifacts/1n92i75t_DSC08992%20%281%29.jpg"
                alt="Gaurav Bhagat — Founder, Consortium Gifts; President, IGC Global Promotions"
                className="absolute inset-0 w-full h-full object-cover object-[50%_30%]"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/45 via-black/10 to-transparent pointer-events-none" />
              <figcaption className="absolute left-4 bottom-4 font-mono text-[10px] tracking-[0.12em] text-cream uppercase bg-ink/70 px-3 py-2">
                Gaurav Bhagat
              </figcaption>
            </motion.div>
          </div>

          {/* body */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="space-y-6 text-[15px] lg:text-[17px] leading-[1.8] text-body max-w-2xl"
            >
              <p>
                <span className="text-ink font-medium">Gaurav Bhagat</span>{" "}
                engineered the spectacular rise of Consortium Gifts, scaling
                it from a modest ₹10,000 credit-card limit into a
                market-leading enterprise trusted by global giants like
                Amazon, BMW, and Salesforce. As an elite industry insider and
                the Global President of a premier international network
                spanning over 40 countries, he has dedicated years to
                championing exceptional businesses&mdash;amplifying their
                impact through a powerful TEDx platform and a massive global
                audience at{" "}
                <a
                  href="https://www.gauravbhagat.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink underline decoration-gold/40 underline-offset-4 hover:decoration-burgundy transition-colors"
                  data-testid="link-gauravbhagat-bio"
                >
                  GauravBhagat.com
                </a>
                .
              </p>
              <p>
                This initiative isn&rsquo;t driven by an external author asking
                for access. It is a masterclass created by a global leader and
                industry peer with the exact platform, voice, and vision
                required to ensure your achievements resonate worldwide&mdash;capturing
                the legacy our industry deserves but has never quite
                documented for itself.
              </p>
            </motion.div>

            {/* stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-14 grid grid-cols-3 gap-6 lg:gap-10 hair-top pt-10"
              data-testid="founder-stats"
            >
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-display font-semibold text-gold text-[1.8rem] sm:text-[2.4rem] leading-none mb-3">
                    {s.n}
                  </p>
                  <p className="font-mono text-[10px] sm:text-[11px] tracking-[0.12em] text-muted-ink uppercase">
                    {s.label}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* role tags */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="mt-10 flex flex-wrap gap-2"
            >
              {ROLES.map((r) => {
                const base =
                  "font-mono text-[10px] tracking-[0.05em] border border-line px-3 py-2 bg-cream-2/50";
                return r.href ? (
                  <a
                    key={r.label}
                    href={r.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${base} text-ink hover:bg-cream-2 hover:border-burgundy transition-colors`}
                    data-testid={`role-link-${r.label.split(" ")[0].toLowerCase()}`}
                  >
                    {r.label}
                  </a>
                ) : (
                  <span
                    key={r.label}
                    className={`${base} text-ink-soft`}
                  >
                    {r.label}
                  </span>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
